import * as React from "react";
import { ChevronsUpDown, Check, Search } from "lucide-react";

import { cn } from "../../lib/utils";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

export function Select({
    options = [],
    value,
    onChange,
    placeholder = "Select...",
    searchPlaceholder = "Search...",
    className = "",
    searchable = false,
    disabled = false
}) {
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const buttonRef = React.useRef(null);
    const dropdownRef = React.useRef(null);

    const filteredOptions =
        !searchable || !search
            ? options
            : options.filter((opt) => opt.label.toLowerCase().includes(search.toLowerCase()));

    const handleSelect = (val) => {
        onChange?.(val);
        setOpen(false);
        setSearch("");
        // Return focus to button after selection
        setTimeout(() => buttonRef.current?.focus(), 0);
    };

    React.useEffect(() => {
        if (!open) setSearch("");
    }, [open]);

    // Close dropdown when clicking outside
    React.useEffect(() => {
        if (!open) return;
        function handleClickOutside(event) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target)
            ) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open]);

    return (
        <div className={cn("relative", className)}>
            <Button
                ref={buttonRef}
                type="button"
                variant="outline"
                className={cn("w-full justify-between min-h-10", disabled && "opacity-50 pointer-events-none")}
                onClick={() => setOpen((v) => !v)}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className="truncate flex-1 text-left mr-2">
                    {options.find((opt) => opt.value === value)?.label || (
                        <span className="text-muted-foreground">{placeholder}</span>
                    )}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
            {open && (
                <div ref={dropdownRef} className="absolute z-[100] mt-1 w-full rounded-md border bg-popover shadow-lg">
                    {searchable && (
                        <div className="p-2">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                    placeholder={searchPlaceholder}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="pl-10"
                                    autoFocus
                                />
                            </div>
                        </div>
                    )}
                    <div className="max-h-64 overflow-auto py-2" tabIndex={-1} role="listbox">
                        {filteredOptions.map((opt, index) => (
                            <div
                                key={"" + opt.value + index}
                                className={cn(
                                    "flex items-center px-3 py-2 cursor-pointer hover:bg-accent",
                                    value === opt.value && "bg-accent"
                                )}
                                onClick={() => handleSelect(opt.value)}
                                role="option"
                                aria-selected={value === opt.value}
                            >
                                <Check
                                    className={cn("mr-2 h-4 w-4 shrink-0", value === opt.value ? "opacity-100" : "opacity-0")}
                                />
                                <span className="truncate">{opt.label}</span>
                            </div>
                        ))}
                        {filteredOptions.length === 0 && (
                            <div className="px-3 py-2 text-muted-foreground text-sm">
                                {search ? "No items found." : "No items available."}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
